import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { selectAgents } from './agentsSelectors';
import { SweepNotification, SweepNotificationVariant } from '../../reducers/notificationsReducer';
import { AgentFileStatus } from '@server/ai';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import { selectActiveAgentId } from './agentsChatsReducer';
import useQueryParams from '../../navigation-utils/useQueryParams';

const useAgentFilesNotifications = (): SweepNotification[] => {
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const agents = useSelector(selectAgents(crmOrgId ?? undefined));
  const activeAgentId = useSelector(selectActiveAgentId);
  const queryParams = useQueryParams();
  const isSettingsOpen = queryParams.has('settings');

  return useMemo(() => {
    if (!agents || !activeAgentId || isSettingsOpen) return [];

    const activeAgent = agents.find((agent) => agent.id === activeAgentId);
    if (!activeAgent) return [];

    const processingFilesCount = (activeAgent.files ?? []).filter(
      (file) => file.status === AgentFileStatus.PROCESSING,
    ).length;

    if (processingFilesCount === 0) return [];

    const message =
      processingFilesCount === 1
        ? 'Processing your file…'
        : `Processing ${processingFilesCount} files…`;

    return [
      {
        key: 'agent-files-processing',
        message,
        variant: SweepNotificationVariant.Loading,
        keepOpen: true,
        hideClose: false,
        autoHideDuration: null,
      },
    ];
  }, [agents, activeAgentId, isSettingsOpen]);
};

export default useAgentFilesNotifications;
